// Walks a freshly-loaded draft and:
//   1. fills in any field that the current schema has but the stored data
//      is missing (so adding a new field to a block doesn't leave existing
//      drafts blank);
//   2. upgrades a small list of known-old default values to the current
//      defaults (so the footer address placeholder etc. flips automatically
//      the first time the draft is opened);
//   3. heals known-bad shapes from prior buggy deploys.
//
// User-edited values always win — we only touch fields whose stored value
// matches a known prior default. Anything that mutates user-authored
// content lives behind a precondition that proves it's a default, never
// a hand-typed value. See CLAUDE.md ("Block schema evolution") before
// adding cases here.

import type { Block } from '@shared/types';
import { BLOCK_SCHEMAS } from '../editor/blocks';

export function normalizeBlocks(blocks: Block[]): Block[] {
  return blocks.map((b) => {
    const schema = BLOCK_SCHEMAS[b.type];
    if (!schema) return b;
    const defaults = schema.defaults() as Record<string, unknown>;
    const data: Record<string, unknown> = { ...defaults, ...(b.data as Record<string, unknown>) };

    if (b.type === 'header') {
      if (data.logoUrl === 'https://penobscotmorning.com/assets/logo-horizontal.png') {
        data.logoUrl = defaults.logoUrl;
      }
    }
    if (b.type === 'footer') {
      if (data.address === '[Mailing address — required by CAN-SPAM]') {
        data.address = defaults.address;
      }
      const socials = data.socials as Array<{ label?: string }> | undefined;
      if (
        Array.isArray(socials) &&
        socials.length === 3 &&
        socials[0]?.label === 'Instagram' &&
        socials[1]?.label === 'Facebook' &&
        socials[2]?.label === 'Tip Line'
      ) {
        data.socials = defaults.socials;
      }
    }
    if (b.type === 'story') {
      // Heal v1-of-inline-images corruption: if the stored row has
      // both `body` and `paragraphs`, the body was injected by an
      // earlier version of normalizeBlocks. `paragraphs` is the
      // user's real content. Drop body so the next save commits a
      // clean row through `storyBodyItems` + `setBody`.
      if (Array.isArray(data.paragraphs) && (data.paragraphs as unknown[]).length > 0 && Array.isArray(data.body)) {
        delete data.body;
      }
    }

    return { ...b, data };
  });
}
